import { Link } from '@tanstack/react-router';
import * as React from 'react';

import type { CampaignListItemDto } from '@/api/campaign-client';
import type {
  AdminAlertDto,
  CampaignHealthDto,
  DashboardCampaignRowDto,
  OperationalCardsDto,
  OperationalViewFilters,
} from '@/api/operational-view-client';
import type {
  SupplierMonitoringRowDto,
  SupplierMonitoringSirenAggregateDto,
} from '@/api/supplier-monitoring-client';
import { useCampaignDetail } from '@/api/use-campaigns';
import { useClientsList } from '@/api/use-clients';
import { useCards, useHealth } from '@/api/use-operational-view';
import { useSupplierMonitoring } from '@/api/use-supplier-monitoring';
import { Button, buttonVariants } from '@/components/ui/button';
import { Panel, StatusDot } from '@/components/ui/panel';
import { campaignStateInBucket, participationStateLabel } from '@/lib/campaign-labels';
import { establishmentStateLabel } from '@/lib/supplier-labels';
import { HORS_JEUX_HINT } from '@/lib/operational-view-labels';
import { cn } from '@/lib/utils';
import {
  formatShortDate,
  MINUS,
  plural,
  StatusLabel,
} from '@/routes/admin/campaign-registry-format';
import {
  RelanceCompositionModal,
  useRelanceComposer,
} from '@/routes/admin/campaigns/campaign-monitoring-screen';

// =============================================================================
// campaign-suppliers-focus.tsx — Section inférieure « Fournisseurs » du registre des campagnes (A-12).
// =============================================================================
// Maquette John 2026-09-16 : carte (gabarit Panel) ouverte par la sélection d'une ligne du registre.
//   Barre de titre — label mono « FOURNISSEURS » + intitulé serif (nom accessible « Fournisseurs — {intitulé} »),
//                    bouton « Fermer ».
//   Colonne gauche (340 px) — PILOTAGE (état, client(s), clôtures de cycle), INDICATEURS (grille 2×2),
//                    ALERTES (texte recomposé des champs structurés, live region : useHealth poll).
//   Colonne droite — SUIVI FOURNISSEURS : bascule SIRET | SIREN, relance des non-soumis, relance de la
//                    sélection (cases à cocher, DBU l.1145), tableau fournisseur / contact / connexion /
//                    CGU · référencement / complétion / actions (Relancer, Chat).
//
// Données : UNIQUEMENT les hooks existants (useCampaignDetail, useClientsList, useHealth, useCards,
// useSupplierMonitoring, useCampaignsDashboard via la ligne enrichie, useSendReminders via useRelanceComposer).
// Aucun endpoint nouveau. La relance réutilise l'orchestration et la modale de composition du suivi temps réel.
// Décision GATE_HUMAN 2026-09-16 (1a) : pas de mention d'historique de participation antérieur.
//
// WCAG 2.1 AA : <h2> (Panel) + <h3> par bloc ; statuts / alertes / retard doublés d'un libellé texte ;
// bascule SIRET | SIREN en boutons aria-pressed ; cases à cocher nommées ; alertes et retour de relance en
// aria-live polite ; <table> avec <caption> et <th scope>.

/** Suivi campagne-level : aucun filtre (référence stable, pas de refetch parasite). */
const NO_FILTERS: OperationalViewFilters = {};

/** Libellé de bloc de la colonne gauche (mono 11px uppercase, parité volet Focus du dashboard). */
const BLOCK_LABEL =
  'font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground';

/** Bouton noir (parité « Ouvrir » actif du dashboard). */
const DARK_BUTTON =
  'border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background';

/** SIRET 14 chiffres → « 539 939 470 00016 » (SIREN groupé 3-3-3 + NIC) ; autre format inchangé. */
export function formatSiret(siret: string): string {
  return /^\d{14}$/.test(siret)
    ? `${siret.slice(0, 3)} ${siret.slice(3, 6)} ${siret.slice(6, 9)} ${siret.slice(9)}`
    : siret;
}

/** Taux de complétion [0,1] → « NN % » ; null (périmètre vide) → « — ». */
function completionLabel(pct: number | null): string {
  return pct === null ? '—' : `${String(Math.round(pct * 100))} %`;
}

export interface CampaignSuppliersFocusProps {
  /** Identifiant DOM de la section (cible aria-controls des boutons de sélection du registre). */
  readonly sectionId: string;
  readonly campaign: CampaignListItemDto;
  /** Ligne enrichie du read-model macroscopique (échéances + jours restants) ; absente si indisponible. */
  readonly enriched: DashboardCampaignRowDto | undefined;
  readonly onClose: () => void;
}

export function CampaignSuppliersFocus({
  sectionId,
  campaign,
  enriched,
  onClose,
}: CampaignSuppliersFocusProps): React.JSX.Element {
  const health = useHealth(campaign.campaignId, NO_FILTERS);
  const cards = useCards(campaign.campaignId, NO_FILTERS);
  const headingId = `${sectionId}-heading`;

  return (
    <div id={sectionId}>
      <Panel
        headingId={headingId}
        title={
          <>
            <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Fournisseurs
            </span>{' '}
            <span className="sr-only">—</span>{' '}
            <span>{campaign.intitule}</span>
          </>
        }
        aside={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-foreground"
          >
            Fermer
          </Button>
        }
      >
        <div className="grid lg:grid-cols-[340px_minmax(0,1fr)]">
          <div className="divide-y divide-border">
            <PilotageBlock campaign={campaign} enriched={enriched} />
            <IndicatorsBlock
              campaignId={campaign.campaignId}
              health={health}
              cards={cards}
            />
            <AlertsBlock
              campaignId={campaign.campaignId}
              health={health}
              fallbackCycleNumber={enriched?.currentCycleNumber ?? null}
            />
          </div>
          <div className="min-w-0 border-t border-border lg:border-l lg:border-t-0">
            <SuppliersTracking campaignId={campaign.campaignId} />
          </div>
        </div>
      </Panel>
    </div>
  );
}

// =============================================================================
// Colonne gauche — Pilotage, Indicateurs, Alertes.
// =============================================================================

function BlockHeading({
  id,
  children,
}: {
  readonly id: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <h3 id={id} className={cn(BLOCK_LABEL, 'flex items-center gap-2')}>
      {children}
    </h3>
  );
}

/** Ligne libellé / valeur alignée à droite (séparateur bas). */
function PilotageRow({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2 last:border-b-0">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right">{children}</dd>
    </div>
  );
}

/** Jours restants d'une clôture : « J−N » (rouge à J−0), « +N j » de retard (rouge). */
function DaysRemaining({ days }: { readonly days: number }): React.JSX.Element {
  if (days < 0) {
    return (
      <span className="text-dash-late">
        +{-days} j<span className="sr-only"> de retard</span>
      </span>
    );
  }
  return (
    <span className={days === 0 ? 'text-dash-late' : 'text-muted-foreground'}>
      J{MINUS}
      {days}
    </span>
  );
}

function PilotageBlock({
  campaign,
  enriched,
}: {
  readonly campaign: CampaignListItemDto;
  readonly enriched: DashboardCampaignRowDto | undefined;
}): React.JSX.Element {
  const detail = useCampaignDetail(campaign.campaignId);
  const clients = useClientsList();
  const headingId = `focus-pilotage-${campaign.campaignId}`;
  // Compte à rebours pertinent tant que la campagne est en cours (parité registre du dashboard).
  const showCountdown = campaignStateInBucket(campaign.state, 'en-cours');

  const raisonSocialeBySiren = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clients.data ?? []) {
      map.set(c.siren, c.raisonSociale);
    }
    return map;
  }, [clients.data]);

  // Échéances : read-model macroscopique (jours restants serveur) ; repli sur les dates du détail sans J−N.
  const deadlines: readonly {
    readonly cycleNumber: number;
    readonly closeAt: string | null;
    readonly daysRemaining: number | null;
  }[] =
    enriched !== undefined
      ? enriched.cycles
      : [...(detail.data?.cycles ?? [])]
          .sort((a, b) => a.cycleNumber - b.cycleNumber)
          .map((c) => ({
            cycleNumber: c.cycleNumber,
            closeAt: c.closeAt,
            daysRemaining: null,
          }));

  const scopes = detail.data?.clientScopes ?? [];

  return (
    <section aria-labelledby={headingId} className="px-6 py-5">
      <BlockHeading id={headingId}>Pilotage</BlockHeading>
      <dl className="mt-2 text-sm">
        <PilotageRow label="État">
          <StatusLabel state={campaign.state} />
        </PilotageRow>
        <PilotageRow label="Client">
          {detail.isPending ? (
            '…'
          ) : scopes.length === 0 ? (
            <span className="text-muted-foreground">Aucun client</span>
          ) : (
            scopes.map((scope, i) => (
              <React.Fragment key={scope.scopeId}>
                {i > 0 ? ', ' : null}
                <span title={`SIREN ${scope.clientSiren}`}>
                  {raisonSocialeBySiren.get(scope.clientSiren) ??
                    scope.clientSiren}
                </span>
              </React.Fragment>
            ))
          )}
        </PilotageRow>
        {deadlines.map((cycle) => (
          <PilotageRow
            key={cycle.cycleNumber}
            label={`Clôture C${String(cycle.cycleNumber)}`}
          >
            <span className="whitespace-nowrap tabular-nums">
              {formatShortDate(cycle.closeAt)}
              {showCountdown && cycle.daysRemaining !== null ? (
                <>
                  {' '}
                  <DaysRemaining days={cycle.daysRemaining} />
                </>
              ) : null}
            </span>
          </PilotageRow>
        ))}
      </dl>
    </section>
  );
}

type HealthQuery = ReturnType<typeof useHealth>;
type CardsQuery = ReturnType<typeof useCards>;

/** Un indicateur de la grille 2×2 : chiffre + libellé muted. */
function Indicator({
  value,
  label,
  hint,
}: {
  readonly value: React.ReactNode;
  readonly label: string;
  /** Sous-libellé discret (aide contextuelle), rendu dans le <dt> sous le libellé. */
  readonly hint?: string;
}): React.JSX.Element {
  return (
    <div className="flex flex-col-reverse gap-1">
      {hint === undefined ? (
        <dt className="text-xs text-muted-foreground">{label}</dt>
      ) : (
        <dt className="text-xs text-muted-foreground">
          <span>{label}</span>
          <span className="block">{hint}</span>
        </dt>
      )}
      <dd className="text-2xl font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function IndicatorsBlock({
  campaignId,
  health,
  cards,
}: {
  readonly campaignId: string;
  readonly health: HealthQuery;
  readonly cards: CardsQuery;
}): React.JSX.Element {
  const headingId = `focus-indicators-${campaignId}`;
  const h: CampaignHealthDto | undefined = health.data;
  const c: OperationalCardsDto | undefined = cards.data;
  return (
    <section aria-labelledby={headingId} className="px-6 py-5">
      <BlockHeading id={headingId}>Indicateurs</BlockHeading>
      {health.isError || cards.isError ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          Erreur de chargement des indicateurs.
        </p>
      ) : null}
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-4">
        <Indicator
          value={h === undefined ? '…' : `${String(h.participationPct)} %`}
          label={
            h === undefined
              ? 'Participation'
              : `Participation · ${String(h.submittedSupplierCount)} / ${String(h.invitedSupplierCount)}`
          }
        />
        <Indicator
          value={
            c === undefined ? (
              '…'
            ) : (
              <>
                {c.collectedCellCount}{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  / {c.expectedCellCount}
                </span>
              </>
            )
          }
          label="Cellules collectées"
        />
        <Indicator
          value={c === undefined ? '…' : `${String(c.cycleProgressPct)} %`}
          label={
            c?.cycleNumber === null || c === undefined
              ? 'Avancement du cycle'
              : `Avancement cycle ${String(c.cycleNumber)}`
          }
        />
        <Indicator
          value={h === undefined ? '…' : h.horsJeuxCount}
          label="TJM hors jeu"
          hint={HORS_JEUX_HINT}
        />
      </dl>
    </section>
  );
}

/**
 * Texte d'alerte recomposé depuis les champs STRUCTURÉS (jamais `message`, jamais « J--N ») :
 *   critical — « Participation anormalement basse (P %) à J−N de l'échéance du cycle C. »
 *   warning  — « Échéance proche (J−N du cycle C) avec une participation de P %. »
 *   dépassée — « … échéance du cycle C dépassée de N j. »
 */
function AlertText({
  alert,
  cycleNumber,
}: {
  readonly alert: AdminAlertDto;
  readonly cycleNumber: number | null;
}): React.JSX.Element {
  const cycle = cycleNumber === null ? '' : ` du cycle ${String(cycleNumber)}`;
  const pct = `${String(alert.participationPct)} %`;
  const days = alert.daysRemaining;
  const late =
    days < 0 ? (
      <span className="text-dash-late">dépassée de {-days} j</span>
    ) : (
      <span className="text-dash-late">
        J{MINUS}
        {days}
      </span>
    );
  if (alert.severity === 'critical') {
    return days < 0 ? (
      <>
        Participation anormalement basse ({pct}) : échéance{cycle} {late}.
      </>
    ) : (
      <>
        Participation anormalement basse ({pct}) à {late} de l’échéance{cycle}.
      </>
    );
  }
  return days < 0 ? (
    <>
      Échéance{cycle} {late} avec une participation de {pct}.
    </>
  ) : (
    <>
      Échéance proche ({late}
      {cycle}) avec une participation de {pct}.
    </>
  );
}

function AlertsBlock({
  campaignId,
  health,
  fallbackCycleNumber,
}: {
  readonly campaignId: string;
  readonly health: HealthQuery;
  readonly fallbackCycleNumber: number | null;
}): React.JSX.Element {
  const headingId = `focus-alerts-${campaignId}`;
  const alerts = health.data?.alerts ?? [];
  const cycleNumber = health.data?.cycleNumber ?? fallbackCycleNumber;
  return (
    <section aria-labelledby={headingId} className="px-6 py-5">
      <BlockHeading id={headingId}>
        Alertes
        {health.data !== undefined ? (
          <span className="rounded border border-dash-critical-fg/30 bg-dash-critical-bg px-1.5 py-px font-sans text-xs font-normal normal-case tracking-normal text-dash-critical-fg">
            {alerts.length}
          </span>
        ) : null}
      </BlockHeading>
      <div aria-live="polite" aria-busy={health.isPending} className="mt-2 text-sm">
        {health.isPending ? (
          <p role="status" className="text-muted-foreground">
            Chargement des alertes…
          </p>
        ) : health.isError ? (
          <p role="alert" className="text-destructive">
            Erreur de chargement des alertes.
          </p>
        ) : alerts.length === 0 ? (
          <p className="text-muted-foreground">Aucune alerte</p>
        ) : (
          <ul className="space-y-2">
            {alerts.map((alert, index) => (
              <li
                key={`${alert.severity}-${String(index)}`}
                className="flex items-baseline gap-2"
              >
                <StatusDot
                  className={cn(
                    'relative -top-px',
                    alert.severity === 'critical'
                      ? 'bg-dash-critical-fg'
                      : 'bg-dash-critical-fg/50',
                  )}
                />
                <span>
                  <span className="sr-only">
                    {alert.severity === 'critical' ? 'Critique' : 'Avertissement'} :{' '}
                  </span>
                  <AlertText alert={alert} cycleNumber={cycleNumber} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// Colonne droite — Suivi fournisseurs.
// =============================================================================

type MonitoringView = 'siret' | 'siren';

function SuppliersTracking({
  campaignId,
}: {
  readonly campaignId: string;
}): React.JSX.Element {
  const query = useSupplierMonitoring(campaignId);
  const [view, setView] = React.useState<MonitoringView>('siret');
  // Sélection multiple (relance groupée par sélection, DBU l.1145) : état de vue éphémère (RULE_REF_006).
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const composer = useRelanceComposer(campaignId, query.data);
  const { relance } = composer;

  const rows = React.useMemo<readonly SupplierMonitoringRowDto[]>(
    () => query.data?.rows ?? [],
    [query.data],
  );
  const submittedCount = rows.filter((r) => r.submitted).length;
  const unsubmittedCount = rows.length - submittedCount;
  const headingId = `focus-suppliers-${campaignId}`;

  const toggle = React.useCallback((siret: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(siret)) {
        next.delete(siret);
      } else {
        next.add(siret);
      }
      return next;
    });
  }, []);

  const toggleAll = React.useCallback((): void => {
    setSelected((prev) =>
      prev.size === rows.length
        ? new Set<string>()
        : new Set(rows.map((r) => r.supplierSiret)),
    );
  }, [rows]);

  return (
    <section aria-labelledby={headingId}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 px-6 py-5">
        <h3 id={headingId} className="flex items-baseline gap-3 font-serif text-lg font-medium">
          Suivi fournisseurs
          {query.data !== undefined ? (
            <span className="font-mono text-sm font-normal text-muted-foreground">
              {plural(rows.length, 'invité')} · {submittedCount} soumis
            </span>
          ) : null}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label="Granularité d’affichage"
            className="inline-flex overflow-hidden rounded-md border border-border"
          >
            {(['siret', 'siren'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => setView(v)}
                className={cn(
                  'h-9 px-3 font-mono text-xs uppercase tracking-wider focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
                  view === v
                    ? 'bg-foreground text-background'
                    : 'bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {v === 'siret' ? 'SIRET' : 'SIREN'}
              </button>
            ))}
          </div>
          {view === 'siret' && selected.size > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="text-foreground"
              disabled={composer.openCycle === null || relance.isPending}
              onClick={() => composer.relanceSelection([...selected])}
            >
              Relancer la sélection · {selected.size}
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={DARK_BUTTON}
            disabled={!composer.canRelance || unsubmittedCount === 0}
            onClick={composer.relanceGroup}
            title={
              composer.openCycle === null
                ? 'Aucun cycle courant ouvert à relancer'
                : unsubmittedCount === 0
                  ? 'Tous les fournisseurs du périmètre ont soumis'
                  : 'Relancer par email tous les fournisseurs non soumis de la campagne'
            }
          >
            Relancer les non-soumis · {unsubmittedCount}
          </Button>
        </div>
      </div>

      {/* Retour de relance (WCAG : région annoncée, jamais couleur seule). */}
      <div aria-live="polite" className="px-6 text-sm">
        {relance.isPending ? (
          <p role="status" className="pb-3 text-muted-foreground">
            Relance en cours…
          </p>
        ) : relance.isError ? (
          <p role="alert" className="pb-3 text-destructive">
            Échec de la relance : {relance.error.message}
          </p>
        ) : relance.isSuccess ? (
          <p role="status" className="pb-3 text-muted-foreground">
            Demande de relance transmise ({String(relance.data.targetedSiretCount)}{' '}
            fournisseur(s) ciblé(s) ; les dossiers déjà soumis sont ignorés).
          </p>
        ) : null}
      </div>

      {query.isPending ? (
        <p role="status" className="border-t border-border px-6 py-4 text-sm text-muted-foreground">
          Chargement du suivi fournisseurs…
        </p>
      ) : query.isError ? (
        <p role="alert" className="border-t border-border px-6 py-4 text-sm text-destructive">
          Erreur de chargement : {query.error.message}
        </p>
      ) : rows.length === 0 ? (
        <p role="status" className="border-t border-border px-6 py-10 text-center text-sm text-muted-foreground">
          Aucun fournisseur participant dans le périmètre.
        </p>
      ) : view === 'siret' ? (
        <SiretTable
          campaignId={campaignId}
          rows={rows}
          selected={selected}
          onToggle={toggle}
          onToggleAll={toggleAll}
          canRelance={composer.canRelance}
          onRelanceSiret={composer.relanceSiret}
        />
      ) : (
        <SirenTable
          aggregates={query.data.sirenAggregates}
          rows={rows}
          canRelance={composer.canRelance}
          onRelanceSiren={composer.relanceSiren}
        />
      )}

      <RelanceCompositionModal
        request={composer.pending}
        subject={composer.subject}
        body={composer.body}
        onSubjectChange={composer.setSubject}
        onBodyChange={composer.setBody}
        isPending={relance.isPending}
        onConfirm={composer.confirmRelance}
        onCancel={composer.closeRelance}
      />
    </section>
  );
}

/** Case « tout sélectionner » : état intermédiaire posé par ref (non exprimable en attribut). */
function SelectAllCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  readonly checked: boolean;
  readonly indeterminate: boolean;
  readonly onChange: () => void;
}): React.JSX.Element {
  const ref = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (ref.current !== null) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="h-3.5 w-3.5 accent-foreground align-middle"
      checked={checked}
      onChange={onChange}
      aria-label="Sélectionner tous les fournisseurs"
    />
  );
}

function SiretTable({
  campaignId,
  rows,
  selected,
  onToggle,
  onToggleAll,
  canRelance,
  onRelanceSiret,
}: {
  readonly campaignId: string;
  readonly rows: readonly SupplierMonitoringRowDto[];
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (siret: string) => void;
  readonly onToggleAll: () => void;
  readonly canRelance: boolean;
  readonly onRelanceSiret: (siret: string) => void;
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto border-t border-border">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Suivi des fournisseurs participants : sélection, fournisseur et état de participation,
          contact référent, dernière connexion, CGU et statut de référencement, complétion, actions
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left">
            <th scope="col" className="w-10 py-3 pl-6 pr-2">
              <SelectAllCheckbox
                checked={selected.size > 0 && selected.size === rows.length}
                indeterminate={selected.size > 0 && selected.size < rows.length}
                onChange={onToggleAll}
              />
            </th>
            <th scope="col" className="whitespace-nowrap px-4 py-3">
              Fournisseur
            </th>
            <th scope="col" className="whitespace-nowrap px-4 py-3">
              Contact
            </th>
            <th scope="col" className="whitespace-nowrap px-4 py-3">
              Connexion
            </th>
            <th scope="col" className="whitespace-nowrap px-4 py-3">
              CGU · Référencement
            </th>
            <th scope="col" className="whitespace-nowrap px-4 py-3">
              Complétion
            </th>
            <th scope="col" className="py-3 pl-4 pr-6">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const name = row.raisonSociale ?? row.supplierSiret;
            return (
              <tr key={row.supplierSiret} className="border-b border-border align-top last:border-b-0">
                <td className="py-3.5 pl-6 pr-2">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 accent-foreground align-middle"
                    checked={selected.has(row.supplierSiret)}
                    onChange={() => onToggle(row.supplierSiret)}
                    aria-label={`Sélectionner le fournisseur ${name}`}
                  />
                </td>
                <th scope="row" className="px-4 py-3.5 text-left font-normal">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{row.raisonSociale ?? '—'}</span>
                    <span className="rounded border border-border px-1.5 py-px text-xs text-muted-foreground">
                      {participationStateLabel(row.participationState).label}
                    </span>
                  </span>
                  <span className="mt-0.5 block whitespace-nowrap font-mono text-xs text-muted-foreground">
                    {formatSiret(row.supplierSiret)}
                  </span>
                </th>
                <td className="px-4 py-3.5">
                  {row.referent === null ? (
                    <span className="text-muted-foreground">Référent inconnu</span>
                  ) : (
                    <>
                      <span className="block">{row.referent.nomComplet ?? '—'}</span>
                      <span className="block text-xs text-muted-foreground">
                        {row.referent.email ?? '—'}
                      </span>
                    </>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3.5 tabular-nums">
                  {row.lastActivityAt === null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    formatShortDate(row.lastActivityAt)
                  )}
                  {row.connectedNoSubmissionOver48h ? (
                    <span className="block text-xs text-dash-late">Sans saisie &gt; 48 h</span>
                  ) : null}
                </td>
                <td className="whitespace-nowrap px-4 py-3.5">
                  {row.cguSigned ? 'Signée' : 'Non signée'}
                  <span className="text-muted-foreground">
                    {' · '}
                    {row.etablissementState === null
                      ? '—'
                      : establishmentStateLabel(row.etablissementState).label}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-3.5">
                  <span className="block font-mono tabular-nums">
                    {completionLabel(row.completionPct)}
                  </span>
                  <span className="block text-xs tabular-nums text-muted-foreground">
                    {row.respondedCoupleCount} / {row.perimeterCoupleCount}
                  </span>
                </td>
                <td className="py-3.5 pl-4 pr-6">
                  <div className="flex items-center justify-end gap-2">
                    {row.submitted ? null : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-foreground"
                        disabled={!canRelance}
                        onClick={() => onRelanceSiret(row.supplierSiret)}
                        aria-label={`Relancer le fournisseur ${name}`}
                      >
                        Relancer
                      </Button>
                    )}
                    {/* R15-COM-09 (c) — deep-link vers le fil (campagne, fournisseur) : route existante. */}
                    <Link
                      to="/admin/conversations"
                      search={{ campaign: campaignId }}
                      className={cn(
                        buttonVariants({ variant: 'outline', size: 'sm' }),
                        'text-foreground',
                      )}
                      aria-label={`Ouvrir le chat avec le fournisseur ${name}`}
                    >
                      Chat
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Vue SIREN : un agrégat par SIREN, repliable sur le détail de ses SIRET (projection de `rows`). */
function SirenTable({
  aggregates,
  rows,
  canRelance,
  onRelanceSiren,
}: {
  readonly aggregates: readonly SupplierMonitoringSirenAggregateDto[];
  readonly rows: readonly SupplierMonitoringRowDto[];
  readonly canRelance: boolean;
  readonly onRelanceSiren: (siren: string) => void;
}): React.JSX.Element {
  const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const rowsBySiren = React.useMemo(() => {
    const map = new Map<string, SupplierMonitoringRowDto[]>();
    for (const r of rows) {
      const list = map.get(r.siren);
      if (list === undefined) {
        map.set(r.siren, [r]);
      } else {
        list.push(r);
      }
    }
    return map;
  }, [rows]);

  const toggle = (siren: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(siren)) {
        next.delete(siren);
      } else {
        next.add(siren);
      }
      return next;
    });
  };

  if (aggregates.length === 0) {
    return (
      <p role="status" className="border-t border-border px-6 py-4 text-sm text-muted-foreground">
        Aucun SIREN participant dans le périmètre.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto border-t border-border">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Synthèse des fournisseurs par SIREN (établissements, soumissions, complétude), repliable
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left">
            <th scope="col" className="whitespace-nowrap py-3 pl-6 pr-4">
              Entreprise
            </th>
            <th scope="col" className="whitespace-nowrap px-4 py-3">
              Établissements
            </th>
            <th scope="col" className="whitespace-nowrap px-4 py-3">
              Soumissions
            </th>
            <th scope="col" className="whitespace-nowrap px-4 py-3">
              Complet
            </th>
            <th scope="col" className="py-3 pl-4 pr-6">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {aggregates.map((agg) => {
            const isExpanded = expanded.has(agg.siren);
            const detailId = `focus-siren-detail-${agg.siren}`;
            const name = agg.raisonSociale ?? agg.siren;
            return (
              <React.Fragment key={agg.siren}>
                <tr className="border-b border-border last:border-b-0">
                  <th scope="row" className="py-3.5 pl-6 pr-4 text-left font-normal">
                    <button
                      type="button"
                      className="flex items-start gap-2 rounded-sm text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      aria-expanded={isExpanded}
                      aria-controls={isExpanded ? detailId : undefined}
                      onClick={() => toggle(agg.siren)}
                    >
                      <span aria-hidden="true" className="select-none text-muted-foreground">
                        {isExpanded ? '▾' : '▸'}
                      </span>
                      <span>
                        <span className="block font-medium">{agg.raisonSociale ?? '—'}</span>
                        <span className="block font-mono text-xs text-muted-foreground">
                          {agg.siren}
                        </span>
                        <span className="sr-only">
                          {isExpanded
                            ? 'Réduire le détail des établissements'
                            : 'Développer le détail des établissements'}
                        </span>
                      </span>
                    </button>
                  </th>
                  <td className="px-4 py-3.5 tabular-nums">{agg.siretCount}</td>
                  <td className="px-4 py-3.5 tabular-nums">
                    {agg.submittedSiretCount} / {agg.siretCount}
                  </td>
                  <td className="px-4 py-3.5">{agg.complet ? 'Complet' : 'Incomplet'}</td>
                  <td className="py-3.5 pl-4 pr-6 text-right">
                    {agg.complet ? null : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="text-foreground"
                        disabled={!canRelance}
                        onClick={() => onRelanceSiren(agg.siren)}
                        aria-label={`Relancer les établissements non soumis du SIREN ${name}`}
                      >
                        Relancer
                      </Button>
                    )}
                  </td>
                </tr>
                {isExpanded ? (
                  <tr id={detailId} className="border-b border-border bg-dash-band last:border-b-0">
                    <td colSpan={5} className="py-3 pl-12 pr-6">
                      <ul className="space-y-1">
                        {(rowsBySiren.get(agg.siren) ?? []).map((r) => (
                          <li
                            key={r.supplierSiret}
                            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5"
                          >
                            <span className="font-mono text-xs">{formatSiret(r.supplierSiret)}</span>
                            <span className="text-xs text-muted-foreground">
                              {participationStateLabel(r.participationState).label} ·{' '}
                              {r.submitted ? 'Soumis' : 'En attente'} ·{' '}
                              {completionLabel(r.completionPct)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
